'use client';

import React, { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface VideoCardProps {
  styleName: string;
  videoUrl: string;
  onOpenLightbox: () => void;
}

const VideoCard: React.FC<VideoCardProps> = ({ styleName, videoUrl, onOpenLightbox }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedData = () => {
      setIsLoading(false);
    };

    const handleError = () => {
      setHasError(true);
      setIsLoading(false);
    };

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('error', handleError);
    };
  }, []);

  const handleMouseEnter = () => {
    if (videoRef.current && !hasError) {
      videoRef.current.play().catch(() => {
        setHasError(true);
      });
      setIsPlaying(true);
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current && !hasError) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const handleMobileToggle = () => {
    if (videoRef.current && !hasError) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play().catch(() => {
          setHasError(true);
        });
        setIsPlaying(true);
      }
    }
  };

  return (
    <div data-component-id="src/app/homepage/components/VideoCard.tsx:72:4" data-component-path="src/app/homepage/components/VideoCard.tsx" data-component-line="72" data-component-end-line="130" data-component-file="VideoCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22group%20relative%20bg-card%20rounded-lg%20overflow-hidden%20border%20border-border%20hover%3Aborder-primary%20transition-all%20duration-300%20cursor-pointer%22%7D" 
      className="group relative bg-card rounded-lg overflow-hidden border border-border hover:border-primary transition-all duration-300 cursor-pointer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onOpenLightbox}
    >
      <div data-component-id="src/app/homepage/components/VideoCard.tsx:78:6" data-component-path="src/app/homepage/components/VideoCard.tsx" data-component-line="78" data-component-end-line="123" data-component-file="VideoCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20aspect-video%20overflow-hidden%22%7D" className="relative aspect-video overflow-hidden">
        {isLoading && (
          <div data-component-id="src/app/homepage/components/VideoCard.tsx:80:10" data-component-path="src/app/homepage/components/VideoCard.tsx" data-component-line="80" data-component-end-line="82" data-component-file="VideoCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20flex%20items-center%20justify-center%20bg-muted%22%7D" className="absolute inset-0 flex items-center justify-center bg-muted">
            <div data-component-id="src/app/homepage/components/VideoCard.tsx:81:12" data-component-path="src/app/homepage/components/VideoCard.tsx" data-component-line="81" data-component-end-line="81" data-component-file="VideoCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-12%20h-12%20border-4%20border-primary%20border-t-transparent%20rounded-full%20animate-spin%22%7D" className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        
        {hasError ? (
          <div data-component-id="src/app/homepage/components/VideoCard.tsx:86:10" data-component-path="src/app/homepage/components/VideoCard.tsx" data-component-line="86" data-component-end-line="89" data-component-file="VideoCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20flex%20flex-col%20items-center%20justify-center%20bg-muted%20text-muted-foreground%22%7D" className="absolute inset-0 flex flex-col items-center justify-center bg-muted text-muted-foreground">
            <Icon data-component-id="src/app/homepage/components/VideoCard.tsx:87:12" data-component-path="src/app/homepage/components/VideoCard.tsx" data-component-line="87" data-component-end-line="87" data-component-file="VideoCard.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ExclamationTriangleIcon%22%2C%22className%22%3A%22mb-2%20opacity-50%22%7D" name="ExclamationTriangleIcon" size={48} variant="outline" className="mb-2 opacity-50" />
            <p data-component-id="src/app/homepage/components/VideoCard.tsx:88:12" data-component-path="src/app/homepage/components/VideoCard.tsx" data-component-line="88" data-component-end-line="88" data-component-file="VideoCard.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%22%2C%22textContent%22%3A%22Video%20unavailable%22%7D" className="text-sm">Video unavailable</p>
          </div>
        ) : (
          <video data-component-id="src/app/homepage/components/VideoCard.tsx:91:10" data-component-path="src/app/homepage/components/VideoCard.tsx" data-component-line="91" data-component-end-line="99" data-component-file="VideoCard.tsx" data-component-name="video" data-component-content="%7B%22elementName%22%3A%22video%22%2C%22className%22%3A%22w-full%20h-full%20object-cover%22%2C%22src%22%3A%22%5Bvar%3AvideoUrl%5D%22%7D"
            ref={videoRef}
            className="w-full h-full object-cover"
            muted
            loop
            playsInline
            preload="metadata"
            src={videoUrl}
          />
        )}

        {/* Mobile Play/Pause Button */}
        <button data-component-id="src/app/homepage/components/VideoCard.tsx:103:8" data-component-path="src/app/homepage/components/VideoCard.tsx" data-component-line="103" data-component-end-line="117" data-component-file="VideoCard.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22md%3Ahidden%20absolute%20top-1%2F2%20left-1%2F2%20-translate-x-1%2F2%20-translate-y-1%2F2%20w-16%20h-16%20bg-black%2F60%20rounded-full%20flex%20items-center%20justify-center%20backdrop-blur-sm%20transition-opacity%20duration-300%22%7D"
          onClick={(e) => {
            e.stopPropagation();
            handleMobileToggle();
          }}
          className="md:hidden absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-black/60 rounded-full flex items-center justify-center backdrop-blur-sm transition-opacity duration-300"
          aria-label={isPlaying ? 'Pause video' : 'Play video'}
        >
          <Icon data-component-id="src/app/homepage/components/VideoCard.tsx:111:10" data-component-path="src/app/homepage/components/VideoCard.tsx" data-component-line="111" data-component-end-line="116" data-component-file="VideoCard.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-white%22%7D" 
            name={isPlaying ? 'PauseIcon' : 'PlayIcon'} 
            size={32} 
            variant="solid" 
            className="text-white"
          />
        </button>

        {/* Expand Icon */}
        <div data-component-id="src/app/homepage/components/VideoCard.tsx:120:8" data-component-path="src/app/homepage/components/VideoCard.tsx" data-component-line="120" data-component-end-line="122" data-component-file="VideoCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-3%20right-3%20w-10%20h-10%20bg-black%2F60%20rounded-full%20flex%20items-center%20justify-center%20opacity-0%20group-hover%3Aopacity-100%20transition-opacity%20duration-300%20backdrop-blur-sm%22%7D" className="absolute top-3 right-3 w-10 h-10 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 backdrop-blur-sm">
          <Icon data-component-id="src/app/homepage/components/VideoCard.tsx:121:10" data-component-path="src/app/homepage/components/VideoCard.tsx" data-component-line="121" data-component-end-line="121" data-component-file="VideoCard.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowsPointingOutIcon%22%2C%22className%22%3A%22text-white%22%7D" name="ArrowsPointingOutIcon" size={20} variant="outline" className="text-white" />
        </div>
      </div>

      <div data-component-id="src/app/homepage/components/VideoCard.tsx:125:6" data-component-path="src/app/homepage/components/VideoCard.tsx" data-component-line="125" data-component-end-line="129" data-component-file="VideoCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%22%7D" className="p-4">
        <h3 data-component-id="src/app/homepage/components/VideoCard.tsx:126:8" data-component-path="src/app/homepage/components/VideoCard.tsx" data-component-line="126" data-component-end-line="128" data-component-file="VideoCard.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-heading%20font-semibold%20text-foreground%20group-hover%3Atext-primary%20transition-colors%20duration-300%22%7D" className="text-lg font-heading font-semibold text-foreground group-hover:text-primary transition-colors duration-300">
          {styleName}
        </h3>
      </div>
    </div>
  );
};

export default VideoCard;